import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ChevronRight, Zap, ShieldCheck, Activity,
  Layers, FlaskConical, CheckCircle2, Phone, Mail, Download, AlertTriangle
} from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';

/* ── 浅色工业风色值 ── */
const ORANGE = 'oklch(0.66 0.19 42)';
const CARD_BG = '#FFFFFF';
const BORDER = '#D0D5E2';
const MUTED_TEXT = '#5A6780';
const DEEP_BG = '#F0F2F5';
const GREEN = 'oklch(0.52 0.16 145)';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};
const stagger = { show: { transition: { staggerChildren: 0.08 } } };

/* ── ESD 分级说明 ── */
const esdGrades = [
  {
    grade: '导电级',
    code: 'Conductive',
    range: '10⁴ ~ 10⁶ Ω',
    color: GREEN,
    desc: '静电泄放最快，适用于对人体 ESD 防护要求极高的 IC 封装与晶圆操作台面。',
    standards: 'EN 61340-5-1 / ANSI/ESD S20.20',
  },
  {
    grade: '静电耗散级',
    code: 'Dissipative',
    range: '10⁶ ~ 10⁹ Ω',
    color: ORANGE,
    desc: '泄放速度适中，防止因泄放过快产生的元器件损伤，是 SMT 产线和精密仪器台最常用的级别。',
    standards: 'EN 61340-5-1 / ANSI/ESD S20.20',
  },
  {
    grade: '抗静电级',
    code: 'Antistatic',
    range: '10⁹ ~ 10¹¹ Ω',
    color: 'oklch(0.65 0.12 260)',
    desc: '防止静电积累，适用于对 ESD 要求相对宽松的包装区域、洁净室通道及一般电子厂房地面。',
    standards: 'GB/T 11287 / IEC 61340',
  },
];

/* ── 技术参数 ── */
const techParams = [
  { test: '表面电阻率（导电级）', standard: 'IEC 61340-2-3 / ASTM D257', value: '10⁴ ~ 10⁶ Ω/sq', note: '导电炭黑配方' },
  { test: '表面电阻率（耗散级）', standard: 'IEC 61340-2-3 / ASTM D257', value: '10⁶ ~ 10⁹ Ω/sq', note: '最常用级别' },
  { test: '静电泄放时间', standard: 'ANSI/ESD STM11.11', value: '< 0.1 秒', note: '100V → <10V 泄放' },
  { test: '拉伸强度', standard: 'ISO 37 / ASTM D412', value: '≥6.0 MPa', note: '导电配方典型值' },
  { test: '断裂伸长率', standard: 'ISO 37 / ASTM D412', value: '≥200%', note: '—' },
  { test: '硬度', standard: 'ISO 48 / ASTM D2240', value: '65±5 Shore A', note: '可定制 60A～75A' },
  { test: '工作温度范围', standard: '—', value: '-20°C 至 +70°C', note: '室内使用场合' },
  { test: '阻燃等级', standard: 'UL 94', value: 'HB 级（标准）/ V-0（选配）', note: '洁净室选配阻燃版' },
];

/* ── 核心特性 ── */
const coreFeatures = [
  {
    icon: Zap,
    title: '稳定的防静电性能',
    titleEn: 'STABLE ESD PROTECTION',
    desc: '导电炭黑均匀分散于橡胶基体，形成连续导电网络，确保表面电阻在 10⁴～10⁹ Ω 区间内长期稳定，不因使用磨损或清洁而显著漂移。',
  },
  {
    icon: ShieldCheck,
    title: '符合国际 ESD 标准',
    titleEn: 'ESD STANDARD COMPLIANT',
    desc: '满足 ANSI/ESD S20.20、EN 61340-5-1、IEC 61340 及 GB/T 11287 标准体系，可为客户提供符合 IEC / ANSI 要求的检测报告，满足审厂需求。',
  },
  {
    icon: Activity,
    title: '快速静电泄放',
    titleEn: 'RAPID DISCHARGE',
    desc: '100V 起始电压在 0.1 秒内泄放至 10V 以下，有效消除人体及设备在操作过程中积累的静电荷，保护精密半导体元器件免遭 ESD 损伤。',
  },
  {
    icon: Layers,
    title: '优良耐磨耐压性',
    titleEn: 'ABRASION RESISTANCE',
    desc: '高质量橡胶基体配合均匀炭黑分散工艺，在拥有导电功能的同时保持良好机械强度与耐磨性，适合 SMT 产线、操作台和地面长期高频使用。',
  },
  {
    icon: FlaskConical,
    title: '耐常见工业溶剂',
    titleEn: 'CHEMICAL RESISTANCE',
    desc: '对异丙醇（IPA）、稀酸碱液及常用电子清洗剂具有良好耐受性，适合定期清洁维护，不影响导电性能和板材完整性。',
  },
];

/* ── 应用场景 ── */
interface AppItem {
  num: string;
  title: string;
  titleEn: string;
  desc: string;
  details: string[];
  img: string | null;
  imgAlt: string;
  grade: string;
}

const applications: AppItem[] = [
  {
    num: '①',
    title: 'SMT 贴片产线工作台',
    titleEn: 'SMT ASSEMBLY WORKBENCH',
    desc: 'SMT 产线的拾取放置工位、检测台和返修台是 ESD 损伤的高风险区域。防静电橡胶台面垫（静电耗散级，10⁶～10⁹ Ω）确保元器件在操作全程处于受控的静电泄放环境，配合接地腕带和接地导线构成完整的 ESD 防护系统。',
    details: ['SMT 拾放工位台垫', '回流焊检测台垫', 'BGA 返修工位台面', 'PCB 测试台防静电垫'],
    img: IMAGES.P19_SMT_WORKBENCH_1_210,
    imgAlt: 'SMT产线ESD防静电工作台',
    grade: '推荐：静电耗散级（10⁶～10⁹ Ω）',
  },
  {
    num: '②',
    title: '半导体洁净室地面铺装',
    titleEn: 'CLEANROOM FLOORING',
    desc: '晶圆厂、半导体封测厂洁净室（Class 100～10000）地面需要同时满足 ESD 控制、洁净度维持和行走舒适性要求。防静电橡胶地板（抗静电级至耗散级）可有效防止人员步行产生的静电积累，配合洁净室等电位接地网络使用。',
    details: ['晶圆厂 FAB 区地面', '封测洁净室通道', '净化走廊防静电铺装', '更衣室及缓冲区地面'],
    img: IMAGES.P19_ANTISTATIC_SHEET_1_207,
    imgAlt: '洁净室防静电橡胶地板',
    grade: '推荐：抗静电至耗散级（10⁶～10¹⁰ Ω）',
  },
  {
    num: '③',
    title: '精密仪器与检测设备台垫',
    titleEn: 'PRECISION INSTRUMENT MATS',
    desc: '示波器、频谱分析仪、高精度测量设备的摆放台面需要防静电保护，同时起到防滑和减震的作用。防静电橡胶垫的高摩擦系数可防止设备滑动，耗散级导电性能确保仪器底部不积累静电。',
    details: ['精密测量仪器台垫', '高频测试设备防静电垫', '光学仪器操作台面', 'EMC 测试室台垫'],
    img: IMAGES.P19_ESD_TEST_NEW_1_177,
    imgAlt: '精密仪器ESD防静电台垫检测',
    grade: '推荐：静电耗散级（10⁶～10⁹ Ω）',
  },
  {
    num: '④',
    title: '防爆区域地面安全铺装',
    titleEn: 'HAZARDOUS AREA FLOORING',
    desc: '石化、制药及危险品存储区域中，人员行走和设备移动产生的静电可能引燃易燃气体或粉尘。导电级防静电橡胶地面（≤10⁶ Ω）配合完善的接地系统，将人体电位迅速降至安全水平，消除静电点火风险。',
    details: ['易燃气体存储区域地面', '制药洁净厂房', '危险品转运通道', '加油站地面防护'],
    img: IMAGES.ESD_ANTISTATIC_FLOOR_EXPLOSIVE,
    imgAlt: '防爆区域防静电地面',
    grade: '推荐：导电级（≤10⁶ Ω），配合接地系统',
  },
];

/* ── 与同类地面材料对比 ── */
const comparison = [
  { prop: 'ESD 防护可靠性', rubber: '★★★★★', pvc: '★★★★☆', epoxy: '★★★☆☆', foam: '★★★☆☆' },
  { prop: '耐磨耐压性', rubber: '★★★★★', pvc: '★★★☆☆', epoxy: '★★★★☆', foam: '★★☆☆☆' },
  { prop: '安装便捷性', rubber: '★★★★★', pvc: '★★★★☆', epoxy: '★★☆☆☆', foam: '★★★★★' },
  { prop: '清洁维护难度', rubber: '★★★★★', pvc: '★★★★☆', epoxy: '★★★★★', foam: '★★★☆☆' },
  { prop: '耐化学品', rubber: '★★★★☆', pvc: '★★★☆☆', epoxy: '★★★★★', foam: '★★☆☆☆' },
  { prop: '使用寿命', rubber: '★★★★★', pvc: '★★★☆☆', epoxy: '★★★★★', foam: '★★☆☆☆' },
];

/* ── 定制能力 ── */
const customFormulas = [
  { dim: 'ESD 等级', standard: '静电耗散级（10⁶～10⁹ Ω）', custom: '可定制导电级 / 耗散级 / 抗静电级', use: '按实际场合电阻要求选型' },
  { dim: '颜色', standard: '黑色（标准）', custom: '绿色 / 灰色（特定配方）', use: '颜色编码区分防护区域' },
  { dim: '表面纹理', standard: '光面', custom: '防滑纹 / 格纹 / 条纹（可定制）', use: '地面铺装防滑要求' },
  { dim: '硬度', standard: '65±5 Shore A', custom: '可向 60A 或 75A 调整', use: '长期站立舒适性或高承载' },
  { dim: '阻燃等级', standard: 'UL 94 HB（标准）', custom: 'UL 94 V-0（选配）', use: '洁净室或防爆区域阻燃要求' },
  { dim: '厚度 / 幅宽', standard: '2.0～10mm / 标准幅宽', custom: '可定制裁切尺寸', use: '工作台垫或异形铺装' },
];

/* ── 产品规格 ── */
const specList = [
  { label: '颜色', value: '黑色（标准）；绿色 / 灰色（特定配方）' },
  { label: '厚度范围', value: '2.0mm 至 10.0mm' },
  { label: '幅宽', value: '0.91m 至 2m（标准），可定制' },
  { label: 'ESD 等级', value: '导电级 / 静电耗散级 / 抗静电级（三档可选）' },
  { label: '表面电阻率', value: '10⁴ ～ 10¹¹ Ω/sq（依等级而定）' },
  { label: '认证标准', value: 'IEC 61340 / ANSI ESD S20.20 / GB/T 11287' },
  { label: '定制服务', value: '裁切、定制颜色、纹理及阻燃等级' },
];

export default function ProductESD() {
  return (
    <div className="bg-background text-foreground min-h-screen">

      {/* ── 面包屑 ── */}
      <div className="border-b border-border" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-xs" style={{ color: MUTED_TEXT }}>
            <Link to="/" className="hover:text-primary transition-colors">首页</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={ROUTE_PATHS.PRODUCTS} className="hover:text-primary transition-colors">产品系列</Link>
            <ChevronRight className="w-3 h-3" />
            <span style={{ color: ORANGE }}>ESD 防静电橡胶板</span>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden" style={{ background: DEEP_BG, minHeight: 420 }}>
        <img
          src={IMAGES.P19_ANTISTATIC_SHEET_1_207}
          alt="ESD防静电橡胶板"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.12 }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, rgba(15,30,53,0.90) 0%, rgba(15,30,53,0.65) 100%)' }}
        />
        <div className="relative container mx-auto px-4 py-16 lg:py-24">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.div variants={fadeUp} className="mb-8">
              <Link
                to={ROUTE_PATHS.PRODUCTS}
                className="inline-flex items-center gap-2 text-sm transition-colors"
                style={{ color: MUTED_TEXT }}
              >
                <ArrowLeft className="w-4 h-4" />
                返回产品列表
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* 左：文字 */}
              <div>
                <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
                  <span
                    className="px-3 py-1 text-xs font-black tracking-[0.2em] uppercase rounded-sm"
                    style={{ background: 'oklch(0.66 0.19 42 / 0.15)', color: ORANGE, border: `1px solid ${ORANGE}` }}
                  >
                    ESD
                  </span>
                  <span className="text-xs tracking-widest uppercase" style={{ color: MUTED_TEXT }}>
                    ELECTROSTATIC DISCHARGE PROTECTION RUBBER
                  </span>
                </motion.div>

                <motion.h1 variants={fadeUp} className="text-4xl lg:text-5xl font-black mb-4 leading-tight">
                  <span style={{ color: ORANGE }}>ESD</span> 防静电橡胶板
                </motion.h1>
                <motion.p variants={fadeUp} className="text-lg font-medium mb-2" style={{ color: 'oklch(0.75 0.010 225)' }}>
                  电子半导体制造的首选静电防护台垫与地面材料
                </motion.p>
                <motion.p variants={fadeUp} className="text-sm leading-relaxed mb-8" style={{ color: MUTED_TEXT }}>
                  专为电子装联、半导体制造及精密仪器操作环境设计的防静电橡胶板，
                  提供导电级（10⁴～10⁶ Ω）、静电耗散级（10⁶～10⁹ Ω）和抗静电级（10⁹～10¹¹ Ω）三个级别，
                  满足 IEC 61340、ANSI/ESD S20.20 及 GB/T 11287 等国际标准体系要求。
                </motion.p>

                <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                  <Link
                    to={ROUTE_PATHS.CONTACT}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-sm"
                    style={{ background: ORANGE, color: '#FFFFFF' }}
                  >
                    <Phone className="w-4 h-4" />
                    立即询价
                  </Link>
                  <Link
                    to={`${ROUTE_PATHS.TECH_SPECS}#esd`}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-sm"
                    style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
                  >
                    查看技术参数
                  </Link>
                </motion.div>
              </div>

              {/* 右：产品图 + 规格卡 */}
              <motion.div variants={fadeUp} className="flex flex-col gap-4">
                <div className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
                  <img
                    src={IMAGES.P19_ESD_TEST_NEW_1_177}
                    alt="ESD防静电橡胶板测试"
                    className="w-full object-cover"
                    style={{ maxHeight: 260, objectPosition: 'center' }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: '表面电阻率', value: '10⁴～10¹¹ Ω', sub: '三档 ESD 等级可选' },
                    { label: '泄放时间', value: '< 0.1 秒', sub: '100V→10V 快速泄放' },
                    { label: '板材厚度', value: '2.0～10mm', sub: '台垫 / 地面铺装' },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className="rounded p-3 text-center"
                      style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                    >
                      <p className="text-[10px] tracking-widest uppercase mb-1" style={{ color: MUTED_TEXT }}>{s.label}</p>
                      <p className="text-sm font-bold leading-tight" style={{ color: ORANGE }}>{s.value}</p>
                      <p className="text-[10px] mt-0.5" style={{ color: MUTED_TEXT }}>{s.sub}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── ESD 分级说明 ── */}
      <section id="esd-grades" className="py-16 lg:py-20" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>ESD CLASSIFICATION</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">三级 ESD 防护等级体系</h2>
              <p className="text-sm text-center mt-2 max-w-2xl mx-auto" style={{ color: MUTED_TEXT }}>
                根据场合的静电防护需求选择对应级别，表面电阻越低，静电泄放越快，防护等级越高。
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {esdGrades.map((g) => (
                <motion.div
                  key={g.grade}
                  variants={fadeUp}
                  className="rounded p-6 flex flex-col gap-4"
                  style={{ background: CARD_BG, border: `1px solid ${g.color}40` }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0"
                      style={{ background: `${g.color}18`, border: `1px solid ${g.color}60` }}
                    >
                      <Zap className="w-5 h-5" style={{ color: g.color }} />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: MUTED_TEXT }}>{g.code}</p>
                      <p className="text-sm font-black">{g.grade}</p>
                    </div>
                  </div>
                  <div
                    className="text-center py-3 rounded-sm"
                    style={{ background: `${g.color}10`, border: `1px solid ${g.color}30` }}
                  >
                    <p className="text-xl font-black font-mono" style={{ color: g.color }}>{g.range}</p>
                    <p className="text-[10px] mt-1" style={{ color: MUTED_TEXT }}>表面电阻率</p>
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: MUTED_TEXT }}>{g.desc}</p>
                  <div className="mt-auto">
                    <p className="text-[10px] font-bold tracking-wider uppercase mb-1" style={{ color: MUTED_TEXT }}>适用标准</p>
                    <p className="text-[10px] font-mono" style={{ color: g.color }}>{g.standards}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 核心特性 ── */}
      <section className="py-16 lg:py-20" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>CORE PROPERTIES</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">核心特性</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {coreFeatures.map((f) => {
                const Icon = f.icon;
                return (
                  <motion.div
                    key={f.title}
                    variants={fadeUp}
                    className="rounded p-5 flex flex-col gap-3"
                    style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                  >
                    <div
                      className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0"
                      style={{ background: 'oklch(0.66 0.19 42 / 0.12)', border: '1px solid oklch(0.66 0.19 42 / 0.3)' }}
                    >
                      <Icon className="w-5 h-5" style={{ color: ORANGE }} />
                    </div>
                    <div>
                      <p className="text-[9px] font-bold tracking-widest uppercase mb-0.5" style={{ color: MUTED_TEXT }}>{f.titleEn}</p>
                      <p className="text-sm font-bold mb-2">{f.title}</p>
                      <p className="text-xs leading-relaxed" style={{ color: MUTED_TEXT }}>{f.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 技术参数 ── */}
      <section className="py-16 lg:py-20" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>TECHNICAL DATA</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">ESD 防静电橡胶板技术参数</h2>
              <p className="text-sm text-center mt-2" style={{ color: MUTED_TEXT }}>
                依据 IEC 61340 / ANSI ESD / GB/T 国际标准体系检测
              </p>
            </motion.div>

            {/* 核心指标高亮 */}
            <motion.div variants={fadeUp} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: '导电级电阻率', value: '10⁴～10⁶ Ω', sub: 'IEC 61340-2-3' },
                { label: '耗散级电阻率', value: '10⁶～10⁹ Ω', sub: '最常用级别' },
                { label: '静电泄放时间', value: '< 0.1s', sub: 'ANSI ESD STM11.11' },
                { label: '工作温度', value: '-20～+70°C', sub: '室内使用' },
              ].map((m) => (
                <div
                  key={m.label}
                  className="rounded p-5 text-center"
                  style={{ background: CARD_BG, border: '1px solid oklch(0.66 0.19 42 / 0.25)' }}
                >
                  <p className="text-2xl font-black mb-1 font-mono" style={{ color: ORANGE }}>{m.value}</p>
                  <p className="text-sm font-semibold mb-1">{m.label}</p>
                  <p className="text-[10px] font-mono" style={{ color: MUTED_TEXT }}>{m.sub}</p>
                </div>
              ))}
            </motion.div>

            {/* 完整参数表 */}
            <motion.div variants={fadeUp} className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                    {['测试项目', '测试标准', '典型数值', '参考说明'].map((h) => (
                      <th key={h} className="px-5 py-3.5 text-left text-xs font-black tracking-widest uppercase" style={{ color: ORANGE }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {techParams.map((row, i) => (
                    <tr
                      key={row.test}
                      style={{ background: i % 2 === 0 ? CARD_BG : '#F0F2F5', borderBottom: `1px solid ${BORDER}` }}
                    >
                      <td className="px-5 py-3.5 font-semibold">{row.test}</td>
                      <td className="px-5 py-3.5 font-mono text-xs" style={{ color: MUTED_TEXT }}>{row.standard}</td>
                      <td className="px-5 py-3.5 font-bold" style={{ color: ORANGE }}>{row.value}</td>
                      <td className="px-5 py-3.5 text-xs" style={{ color: MUTED_TEXT }}>{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 应用场景 ── */}
      <section className="py-16 lg:py-20" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-12">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>ESD APPLICATIONS</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">典型应用场景</h2>
            </motion.div>

            <div className="space-y-8">
              {applications.map((app, idx) => (
                <motion.div
                  key={app.num}
                  variants={fadeUp}
                  className="rounded overflow-hidden"
                  style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                >
                  <div className={`grid grid-cols-1 lg:grid-cols-2 ${idx % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                    <div
                      className={`relative overflow-hidden ${idx % 2 === 1 ? 'lg:order-2' : ''}`}
                      style={{ minHeight: 260 }}
                    >
                      {app.img ? (
                        <img
                          src={app.img}
                          alt={app.imgAlt}
                          className="absolute inset-0 w-full h-full object-cover"
                          style={{ opacity: 0.88 }}
                        />
                      ) : (
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center gap-2"
                          style={{ background: '#ECEEF2', border: `2px dashed ${BORDER}` }}
                        >
                          <AlertTriangle className="w-8 h-8" style={{ color: MUTED_TEXT }} />
                          <p className="text-xs font-mono" style={{ color: MUTED_TEXT }}>图片位置保留</p>
                        </div>
                      )}
                      <div
                        className="absolute top-4 left-4 w-10 h-10 rounded-sm flex items-center justify-center font-black text-lg z-10"
                        style={{ background: ORANGE, color: '#FFFFFF' }}
                      >
                        {app.num}
                      </div>
                    </div>
                    <div className={`p-8 flex flex-col justify-center ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <p className="text-[10px] font-bold tracking-widest uppercase mb-2" style={{ color: ORANGE }}>{app.titleEn}</p>
                      <h3 className="text-xl font-black mb-2">{app.title}</h3>
                      <div
                        className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-sm mb-4 self-start"
                        style={{ background: 'oklch(0.65 0.18 145 / 0.12)', color: GREEN, border: '1px solid oklch(0.65 0.18 145 / 0.3)' }}
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        {app.grade}
                      </div>
                      <p className="text-sm leading-relaxed mb-6" style={{ color: MUTED_TEXT }}>{app.desc}</p>
                      <div>
                        <p className="text-xs font-bold tracking-wider uppercase mb-3" style={{ color: MUTED_TEXT }}>典型用途</p>
                        <div className="grid grid-cols-2 gap-2">
                          {app.details.map((d) => (
                            <div key={d} className="flex items-center gap-2 text-xs">
                              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ORANGE }} />
                              {d}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 与同类材料对比 ── */}
      <section className="py-16" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>MATERIAL COMPARISON</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">防静电橡胶 vs 同类 ESD 地面材料</h2>
            </motion.div>

            <motion.div variants={fadeUp} className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                    {['性能维度', 'ESD 橡胶（本品）', 'PVC 防静电地板', '环氧 ESD 涂层', 'ESD 泡棉垫'].map((h, i) => (
                      <th key={h} className="px-5 py-3.5 text-left text-xs font-black tracking-widest uppercase"
                        style={{ color: i === 1 ? ORANGE : 'oklch(0.70 0.015 225)' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row, i) => (
                    <tr key={row.prop} style={{ background: i % 2 === 0 ? CARD_BG : '#F0F2F5', borderBottom: `1px solid ${BORDER}` }}>
                      <td className="px-5 py-3.5 font-semibold">{row.prop}</td>
                      <td className="px-5 py-3.5 font-bold" style={{ color: ORANGE }}>{row.rubber}</td>
                      <td className="px-5 py-3.5" style={{ color: MUTED_TEXT }}>{row.pvc}</td>
                      <td className="px-5 py-3.5" style={{ color: MUTED_TEXT }}>{row.epoxy}</td>
                      <td className="px-5 py-3.5" style={{ color: MUTED_TEXT }}>{row.foam}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 定制 + 规格 ── */}
      <section className="py-16 lg:py-20" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>CUSTOM & SPECS</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">定制能力与产品规格</h2>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              {/* 定制表 */}
              <motion.div variants={fadeUp}>
                <h3 className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: MUTED_TEXT }}>可调整配置维度</h3>
                <div className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                        {['调整维度', '标准配置', '可调方向'].map((h) => (
                          <th key={h} className="px-4 py-3 text-left font-black tracking-widest uppercase" style={{ color: ORANGE }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {customFormulas.map((row, i) => (
                        <tr key={row.dim} style={{ background: i % 2 === 0 ? CARD_BG : '#F0F2F5', borderBottom: `1px solid ${BORDER}` }}>
                          <td className="px-4 py-3 font-semibold">{row.dim}</td>
                          <td className="px-4 py-3 font-mono" style={{ color: 'oklch(0.78 0.012 225)' }}>{row.standard}</td>
                          <td className="px-4 py-3 font-bold" style={{ color: ORANGE }}>{row.custom}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>

              {/* 规格 + 图 */}
              <motion.div variants={fadeUp} className="flex flex-col gap-6">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
                    <img src={IMAGES.P19_ANTISTATIC_SHEET_1_207} alt="ESD防静电板材" className="w-full h-40 object-cover" />
                  </div>
                  <div className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
                    <img src={IMAGES.P19_ESD_TEST_NEW_1_177} alt="ESD测试设备" className="w-full h-40 object-cover" />
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: MUTED_TEXT }}>标准产品规格</h3>
                  <div className="space-y-2">
                    {specList.map((spec) => (
                      <div
                        key={spec.label}
                        className="flex items-start gap-4 text-sm py-2.5"
                        style={{ borderBottom: `1px solid ${BORDER}` }}
                      >
                        <span className="w-28 flex-shrink-0 text-xs font-bold tracking-wider uppercase pt-0.5" style={{ color: MUTED_TEXT }}>
                          {spec.label}
                        </span>
                        <span className="font-semibold">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-14" style={{ background: '#F0F2F5', borderTop: `1px solid ${BORDER}` }}>
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.div variants={fadeUp} className="mb-4">
              <span className="text-xs font-bold tracking-widest uppercase" style={{ color: ORANGE }}>GET A QUOTE</span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-2xl lg:text-3xl font-black mb-4">
              <>需要 ESD 防静电橡胶板？<br />我们提供选型咨询与检测报告</>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-sm mb-8" style={{ color: MUTED_TEXT }}>
              说明您的工况（所需防护等级、使用面积、规格尺寸），技术顾问将为您提供选型建议并安排样品。
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to={ROUTE_PATHS.CONTACT}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-black rounded-sm"
                style={{ background: ORANGE, color: '#FFFFFF' }}
              >
                <Mail className="w-4 h-4" />
                提交询价
              </Link>
              <a
                href="tel:13426031477"
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm"
                style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
              >
                <Phone className="w-4 h-4" />
                13426031477
              </a>
              <Link
                to={ROUTE_PATHS.SELECTION}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm"
                style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
              >
                <Download className="w-4 h-4" />
                查看选型矩阵
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  );
}
